/**
 * Toast bridge — lets non-component code (axios interceptors, service modules)
 * raise user-facing toasts.
 *
 * PrimeVue's useToast() can only be called inside component setup. The
 * ToastBridge.vue component calls setToastInstance() once on mount, then
 * anywhere in the codebase can call notifyError() / notifyWarning() / etc.
 *
 * If no app has mounted ToastBridge yet (e.g. a request fires during initial
 * boot before Vue is ready), calls are silently dropped — the backend report
 * still goes out, so nothing is lost.
 */

type ToastSeverity = 'success' | 'info' | 'warn' | 'error'

interface ToastPayload {
  severity: ToastSeverity
  summary: string
  detail?: string
  life?: number
}

interface ToastInstance {
  add: (payload: ToastPayload) => void
}

let instance: ToastInstance | null = null
const recent = new Map<string, number>()
const DEDUPE_MS = 4000

export function setToastInstance(t: ToastInstance): void {
  instance = t
}

function dedupe(key: string): boolean {
  const now = Date.now()
  const last = recent.get(key)
  if (last && now - last < DEDUPE_MS) return false
  recent.set(key, now)
  if (recent.size > 50) {
    for (const [k, ts] of recent) {
      if (now - ts > DEDUPE_MS * 2) recent.delete(k)
    }
  }
  return true
}

export function notifyError(summary: string, detail?: string): void {
  if (!instance) return
  if (!dedupe(`error:${summary}:${detail ?? ''}`)) return
  instance.add({ severity: 'error', summary, detail, life: 6000 })
}

export function notifyWarning(summary: string, detail?: string): void {
  if (!instance) return
  if (!dedupe(`warn:${summary}:${detail ?? ''}`)) return
  instance.add({ severity: 'warn', summary, detail, life: 5000 })
}

export function notifyInfo(summary: string, detail?: string): void {
  if (!instance) return
  if (!dedupe(`info:${summary}:${detail ?? ''}`)) return
  instance.add({ severity: 'info', summary, detail, life: 4000 })
}
